import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import type { BulkMediaApi } from './api'
import { relTime } from './format'
import { formatBytes } from './stage'
import type { ItemStatus, JobDetail, JobSummary } from './types'
import { LoadingBlock } from '../components/Spinner'

const RUNNING_POLL_MS = 4000
const IDLE_POLL_MS = 30000
const DETAIL_POLL_MS = 3000

interface Props {
  api: BulkMediaApi
  /** Bumped when this page starts a job; the list reloads and opens it. */
  created?: { id: string; n: number } | null
}

const finished = (counts: JobSummary['item_counts']) => (counts.done ?? 0) + (counts.failed ?? 0) + (counts.cancelled ?? 0)

/** The last 7 days of Bulk Media jobs (everyone's), newest first; each opens to its Account × file grid. */
export function JobsPanel({ api, created }: Props) {
  const { t } = useTranslation('bulkMedia')
  const [jobs, setJobs] = useState<JobSummary[] | null>(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setJobs(await api.jobs())
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [api])

  useEffect(() => {
    if (created) setOpen(created.id)
    void load()
  }, [created, load])

  const anyRunning = (jobs ?? []).some((j) => j.status === 'running')
  useEffect(() => {
    const id = window.setInterval(() => void load(), anyRunning ? RUNNING_POLL_MS : IDLE_POLL_MS)
    return () => window.clearInterval(id)
  }, [anyRunning, load])

  const onChanged = useCallback((d: JobDetail) => {
    setJobs((list) => (list ?? []).map((j) => (j.id === d.id ? { ...j, ...summaryOf(d) } : j)))
  }, [])

  return (
    <section className="bm-jobs" aria-label={t('jobs.title')}>
      <header className="bm-jobs-head">
        <h2 className="bm-h2">{t('jobs.title')}</h2>
        <span className="bm-muted">{t('jobs.window')}</span>
      </header>
      {error && <p className="bm-error">{error}</p>}
      {!jobs && !error && <LoadingBlock pad="16px 0" size={20} label={t('loading')} />}
      {jobs && !jobs.length && <p className="bm-muted bm-empty">{t('jobs.none')}</p>}
      <div className="bm-joblist">
        {(jobs ?? []).map((j) => (
          <JobCard key={j.id} api={api} job={j} open={open === j.id} onToggle={() => setOpen(open === j.id ? null : j.id)} onChanged={onChanged} />
        ))}
      </div>
    </section>
  )
}

function summaryOf(d: JobDetail): JobSummary {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { files, account_rows, items_grid, ...summary } = d
  return summary
}

function JobCard({
  api,
  job,
  open,
  onToggle,
  onChanged,
}: {
  api: BulkMediaApi
  job: JobSummary
  open: boolean
  onToggle: () => void
  onChanged: (d: JobDetail) => void
}) {
  const { t, i18n } = useTranslation('bulkMedia')
  const upload = job.kind === 'upload'
  const files = upload ? job.items / Math.max(1, job.accounts) : 0
  const pct = upload && job.items ? Math.round((finished(job.item_counts) / job.items) * 100) : job.status === 'running' ? 0 : 100
  const title = upload
    ? t('jobs.uploadTitle', { count: files, accounts: job.accounts })
    : t('jobs.categoriesTitle', { count: job.category_names.length, accounts: job.accounts })

  return (
    <article className={`bm-job is-${job.status} ${open ? 'is-open' : ''}`}>
      <button type="button" className="bm-job-head" onClick={onToggle} aria-expanded={open}>
        <FeatherIcon name={upload ? 'upload' : 'folder-plus'} size={16} className="bm-job-icon" />
        <span className="bm-job-text">
          <span className="bm-job-title">{title}</span>
          <span className="bm-job-meta">
            {relTime(job.created_at, i18n.language)}
            {job.source === 'vault' && ` · ${t('jobs.fromVault')}`}
            {job.category_names.length > 0 && ` · ${job.category_names.join(', ')}`}
          </span>
        </span>
        <span className={`bm-status is-${job.status}`}>{t(`jobs.status.${job.status}`)}</span>
        <FeatherIcon name={open ? 'chevron-down' : 'chevron-right'} size={16} className="bm-muted" />
      </button>
      {job.status === 'running' && (
        <span className="bm-bar bm-bar--job" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${Math.max(pct, 3)}%` }} />
        </span>
      )}
      {(job.accounts_failed > 0 || job.accounts_waiting > 0 || (job.item_counts.failed ?? 0) > 0) && (
        <p className="bm-job-flags">
          {job.accounts_waiting > 0 && <span>{t('jobs.waiting', { count: job.accounts_waiting })}</span>}
          {job.accounts_failed > 0 && <span className="bm-error-text">{t('jobs.accountsFailed', { count: job.accounts_failed })}</span>}
          {(job.item_counts.failed ?? 0) > 0 && <span className="bm-error-text">{t('jobs.filesFailed', { count: job.item_counts.failed })}</span>}
        </p>
      )}
      {open && <JobDetailView api={api} jobId={job.id} running={job.status === 'running'} onChanged={onChanged} />}
    </article>
  )
}

const CELL_ICON: Record<ItemStatus, string> = {
  pending: 'clock',
  converted: 'upload',
  in_vault: 'layers',
  done: 'check',
  failed: 'alert-circle',
  cancelled: 'x',
}

function JobDetailView({ api, jobId, running, onChanged }: { api: BulkMediaApi; jobId: string; running: boolean; onChanged: (d: JobDetail) => void }) {
  const { t, i18n } = useTranslation('bulkMedia')
  const [d, setD] = useState<JobDetail | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<'retry' | 'cancel' | null>(null)
  const changed = useRef(onChanged)
  changed.current = onChanged

  const load = useCallback(async () => {
    try {
      const next = await api.job(jobId)
      setD(next)
      setError('')
      changed.current(next)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [api, jobId])

  const live = running || d?.status === 'running'
  useEffect(() => {
    void load()
    if (!live) return
    const id = window.setInterval(() => void load(), DETAIL_POLL_MS)
    return () => window.clearInterval(id)
  }, [live, load])

  const act = async (kind: 'retry' | 'cancel') => {
    if (kind === 'cancel' && !window.confirm(t('jobs.cancelConfirm'))) return
    setBusy(kind)
    try {
      const next = await (kind === 'retry' ? api.retryJob(jobId) : api.cancelJob(jobId))
      setD(next)
      changed.current(next)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  if (!d) return <div className="bm-detail">{error ? <p className="bm-error">{error}</p> : <LoadingBlock pad="16px 0" size={20} label={t('loading')} />}</div>

  const cell = new Map(d.items_grid.map((i) => [`${i.account_id}|${i.file_id}`, i]))
  const canRetry =
    d.status !== 'cancelled' &&
    (d.account_rows.some((a) => a.status === 'failed' || a.leftover_post_ids.length > 0) || d.items_grid.some((i) => i.status === 'failed'))

  return (
    <div className="bm-detail">
      {error && <p className="bm-error">{error}</p>}
      <div className="bm-detail-actions">
        {canRetry && (
          <button type="button" className="bm-btn" disabled={busy !== null} onClick={() => void act('retry')}>
            <FeatherIcon name="rotate-cw" size={14} />
            {t('jobs.retry')}
          </button>
        )}
        {d.status === 'running' && (
          <button type="button" className="bm-btn bm-btn--ghost" disabled={busy !== null} onClick={() => void act('cancel')}>
            {t('jobs.cancel')}
          </button>
        )}
      </div>
      <div className="bm-gridwrap">
        <table className="bm-grid">
          <thead>
            <tr>
              <th scope="col">{t('jobs.account')}</th>
              {d.files.map((f) => (
                <th key={f.id} scope="col" title={`${f.filename} · ${formatBytes(f.size)}`}>
                  <span className="bm-grid-file">
                    <FeatherIcon name={f.media_type === 'video' ? 'film' : 'image'} size={12} />
                    {f.filename}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.account_rows.map((a) => (
              <tr key={a.account_id}>
                <th scope="row">
                  <span className="bm-grid-acc">
                    <span className="bm-grid-accname">{a.username ? `@${a.username}` : a.name}</span>
                    <span className={`bm-status is-${a.status}`}>{t(`jobs.accountStatus.${a.status}`)}</span>
                  </span>
                  {a.status === 'waiting' && a.next_attempt_at && (
                    <span className="bm-grid-note">{t('jobs.nextAttempt', { when: relTime(a.next_attempt_at, i18n.language) })}</span>
                  )}
                  {a.error && <span className="bm-grid-note bm-error-text">{a.error}</span>}
                  {a.categories.some((c) => c.created) && (
                    <span className="bm-grid-note">
                      {t('jobs.created', { names: a.categories.filter((c) => c.created).map((c) => c.name).join(', ') })}
                    </span>
                  )}
                  {a.categories.some((c) => c.duplicate) && (
                    <span className="bm-grid-note">
                      {t('jobs.duplicate', { names: a.categories.filter((c) => c.duplicate).map((c) => c.name).join(', ') })}
                    </span>
                  )}
                  {a.leftover_post_ids.length > 0 && <span className="bm-grid-note bm-error-text">{t('jobs.leftover', { count: a.leftover_post_ids.length })}</span>}
                </th>
                {d.files.map((f) => {
                  const it = cell.get(`${a.account_id}|${f.id}`)
                  if (!it) return <td key={f.id} />
                  const label = it.reused ? t('jobs.itemStatus.reused') : t(`jobs.itemStatus.${it.status}`)
                  return (
                    <td key={f.id}>
                      <span className={`bm-cell is-${it.status}`} title={it.error || label}>
                        <FeatherIcon name={CELL_ICON[it.status]} size={12} />
                        {label}
                      </span>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
